import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Calendar, MapPin, ArrowRight, Users, Heart, Leaf, Music, Phone, MessageCircle, Mail, CheckCircle, Loader2, Star, Trophy, TreePine, Ticket, HandHeart, Mountain, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { OptimizedImage } from '../components/OptimizedImage';

const API = `${window.location.origin}/api`;
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_horse-events-fr/artifacts/gqg2n92m_Brious%27OFolies_08-25_Endurance_258_HighRes%20%281%29.jpg";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_dd86d20d-7215-40c9-ab96-b556c05959d7/artifacts/n0vf802x_Plan%20de%20travail%2040.png";

// Chiffres clés
const keyStats = [
  { value: "10+", label: "Événements/an", icon: Calendar },
  { value: "500+", label: "Visiteurs accueillis", icon: Users },
  { value: "30+", label: "Bénévoles mobilisés", icon: HandHeart },
  { value: "25ha", label: "De terrain", icon: TreePine },
];

// Options newsletter
const newsletterInterests = [
  { id: "endurance", label: "Course d'endurance équestre" },
  { id: "concerts", label: "Concerts et spectacles" },
  { id: "nature", label: "Activités nature et randonnées" },
  { id: "famille", label: "Événements familiaux" },
  { id: "pedagogie", label: "Activités pédagogiques" },
  { id: "benevole", label: "Devenir bénévole" },
];

// Saisons
const seasons = [
  { 
    name: "Printemps", 
    color: "from-lime-500 to-green-500",
    events: ["Stages équitation", "Randonnées nature", "Chasse aux œufs", "Soirées country"]
  },
  { 
    name: "Été", 
    color: "from-amber-500 to-orange-500",
    events: ["Concerts de Chœurs", "Course d'endurance championnat départemental", "Brocantes"]
  },
  { 
    name: "Automne", 
    color: "from-orange-500 to-red-500",
    events: ["Rando Halloween", "Ciné-goûter", "Course d'endurance"]
  },
  { 
    name: "Hiver", 
    color: "from-blue-500 to-indigo-500",
    events: ["Ciné-goûter", "Concerts intimistes", "Ateliers", "Jeux"]
  },
];

// Sites touristiques
const touristSites = [
  { name: "Site Gallo-Romain de Sanxay", distance: "3 km", icon: Building },
  { name: "Futuroscope", distance: "35 km", icon: Star },
  { name: "Marais Poitevin", distance: "60 km", icon: Mountain },
  { name: "La Rochelle", distance: "100 km", icon: Trophy },
];

export default function Home() {
  const { t, i18n } = useTranslation();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterInterestsSelected, setNewsletterInterestsSelected] = useState([]);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  const widgetRef = useRef(null);

  const values = [
    { icon: Heart, title: t('homeExt.benevolence'), description: t('homeExt.benevolenceDesc') },
    { icon: Users, title: t('homeExt.conviviality'), description: t('homeExt.convivialityDesc') },
    { icon: Leaf, title: t('homeExt.respect'), description: t('homeExt.respectDesc') },
    { icon: Music, title: t('homeExt.culture'), description: t('homeExt.cultureDesc') },
  ];

  const keyStats = [
    { value: "10+", label: t('homeExt.eventsPerYear'), icon: Calendar },
    { value: "500+", label: t('homeExt.visitorsWelcomed'), icon: Users },
    { value: "30+", label: t('homeExt.volunteersActive'), icon: HandHeart },
    { value: "25ha", label: t('homeExt.landArea'), icon: TreePine },
  ];

  const newsletterInterests = [
    { id: "endurance", label: t('homeExt.nlEndurance') },
    { id: "concerts", label: t('homeExt.nlConcerts') },
    { id: "nature", label: t('homeExt.nlNature') },
    { id: "famille", label: t('homeExt.nlFamily') },
    { id: "pedagogie", label: t('homeExt.nlPedagogy') },
    { id: "benevole", label: t('homeExt.nlVolunteer') },
  ];

  const seasons = [
    { name: t('homeExt.spring'), color: "from-lime-500 to-green-500", events: t('homeExt.springEvents', { returnObjects: true }) },
    { name: t('homeExt.summer'), color: "from-amber-500 to-orange-500", events: t('homeExt.summerEvents', { returnObjects: true }) },
    { name: t('homeExt.autumn'), color: "from-orange-500 to-red-500", events: t('homeExt.autumnEvents', { returnObjects: true }) },
    { name: t('homeExt.winter'), color: "from-blue-500 to-indigo-500", events: t('homeExt.winterEvents', { returnObjects: true }) },
  ];

  const touristSites = [
    { name: t('homeExt.galloRomanSite'), distance: "3 km", icon: Building },
    { name: t('homeExt.futuroscope'), distance: "35 km", icon: Star },
    { name: t('homeExt.maraisPoitevin'), distance: "60 km", icon: Mountain },
    { name: t('homeExt.laRochelle'), distance: "100 km", icon: Trophy },
  ];

  useEffect(() => {
    const seedAndFetch = async () => {
      try {
        await axios.post(`${API}/seed-events`);
        await axios.post(`${API}/seed-news`);
        await axios.post(`${API}/seed-partners`);
      } catch (e) {}
    };

    seedAndFetch();

    // Remove and reload Billetweb script to force re-init on SPA navigation
    const existingScript = document.querySelector('script[src="https://www.billetweb.fr/js/export.js"]');
    if (existingScript) existingScript.remove();
    const bwScript = document.createElement('script');
    bwScript.src = 'https://www.billetweb.fr/js/export.js';
    bwScript.async = true;
    document.body.appendChild(bwScript);
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterLoading(true);

    try {
      await axios.post(`${API}/newsletter`, {
        email: newsletterEmail,
        interests: newsletterInterestsSelected
      });
      toast.success(t('homeExt.welcomeMsg'), {
        description: t('homeExt.welcomeDesc')
      });
      setNewsletterSubmitted(true);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.success(t('homeExt.prefsUpdated'), {
          description: t('homeExt.prefsUpdatedDesc')
        });
        setNewsletterSubmitted(true);
      } else {
        toast.error(t('common.error'), {
          description: t('contactExt.errorDesc')
        });
      }
    } finally {
      setNewsletterLoading(false);
    }
  };

  const toggleInterest = (interestId) => {
    setNewsletterInterestsSelected(prev => 
      prev.includes(interestId)
        ? prev.filter(id => id !== interestId)
        : [...prev, interestId]
    );
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section - Amélioré avec 3 CTA */}
      <section className="min-h-screen pt-24 bg-gradient-to-br from-white via-fuchsia-50/30 to-lime-50/30 relative" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="order-2 lg:order-1">
              <img
                src={LOGO_URL}
                alt="Brious'O Folies"
                className="h-24 md:h-32 w-auto mb-8"
                data-testid="hero-logo"
              />
              <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-zinc-900 mb-6 leading-tight">
                {t('homeExt.heroTitle1')} <span className="text-fuchsia-600">Brious'O Folies</span> {t('homeExt.heroTitle2')}
              </h1>
              <p className="text-lg md:text-xl text-zinc-600 mb-8 leading-relaxed max-w-xl">
                {t('homeExt.heroSubtitle')}{' '}
                <span className="text-lime-600 font-medium">{t('homeExt.paysMelusin')}</span>
              </p>
              
              {/* 3 CTA principaux */}
              <div className="flex flex-wrap gap-3 mb-8">
                <Link to="/evenements">
                  <Button className="btn-primary text-base px-6 py-5 w-full sm:w-auto" data-testid="hero-events-btn">
                    <Ticket className="mr-2 h-5 w-5" />
                    {t('homeExt.seeEvents')}
                  </Button>
                </Link>
                <Link to="/adhesion">
                  <Button className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-full px-6 py-5 text-base w-full sm:w-auto" data-testid="hero-adhesion-btn">
                    <Heart className="mr-2 h-5 w-5" />
                    {t('homeExt.becomeMember')}
                  </Button>
                </Link>
                <Link to="/benevoles">
                  <Button variant="outline" className="border-2 border-lime-500 text-lime-600 hover:bg-lime-50 rounded-full px-6 py-5 text-base w-full sm:w-auto" data-testid="hero-benevole-btn">
                    <HandHeart className="mr-2 h-5 w-5" />
                    {t('homeExt.becomeVolunteer')}
                  </Button>
                </Link>
              </div>
              <a href="#newsletter-section" className="inline-flex items-center text-fuchsia-600 hover:text-fuchsia-700 font-medium">
                <Mail className="mr-2 h-4 w-4" />
                {t('homeExt.receiveNews')}
                <ArrowRight className="ml-1 h-4 w-4" />
              </a>
            </div>
            
            {/* Right Image */}
            <div className="order-1 lg:order-2 relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-fuchsia-500/20">
                <OptimizedImage
                  src={HERO_IMAGE}
                  alt="Équitation au Domaine de la Briouse"
                  className="w-full h-[400px] lg:h-[550px]"
                  priority={true}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              </div>
              {/* Decorative elements */}
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-lime-400 rounded-full opacity-60 blur-2xl" />
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-fuchsia-400 rounded-full opacity-40 blur-3xl" />
              
              {/* Floating card */}
              <div className="absolute bottom-8 left-8 right-8 bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
                <p className="text-sm text-zinc-600">📍 Domaine de la Briouse</p>
                <p className="font-semibold text-zinc-900">Sanxay (86) - Pays Mélusin</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chiffres Clés - Preuve sociale */}
      <section className="py-12 bg-zinc-900 text-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {keyStats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <stat.icon className="h-8 w-8 mx-auto mb-3 text-fuchsia-400" />
                <p className="text-4xl font-bold text-white mb-1">{stat.value}</p>
                <p className="text-zinc-400">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Events Section - Widget Billetweb */}
      <section className="py-20 bg-gradient-to-b from-white to-fuchsia-50/30" data-testid="events-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-fuchsia-100 text-fuchsia-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">
              {t('homeExt.dontMiss')}
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              {t('homeExt.upcomingEvents')}
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              {t('homeExt.upcomingEventsDesc')}
            </p>
          </div>

          <div
            ref={widgetRef}
            className="rounded-2xl overflow-hidden shadow-lg border border-zinc-200 bg-white"
            data-testid="home-billetweb-widget"
          >
            <a
              title="Vente de billets en ligne"
              href="https://www.billetweb.fr/multi_event.php?multi=44773"
              className="shop_frame"
              target="_blank"
              rel="noopener noreferrer"
              data-src="https://www.billetweb.fr/multi_event.php?multi=44773"
              data-max-width="100%"
              data-initial-height="600"
              data-scrolling="no"
              data-id="44773"
              data-resize="1"
            >
              Vente de billets en ligne
            </a>
          </div>

          <div className="text-center mt-12">
            <Link to="/evenements">
              <Button className="btn-primary text-lg px-10 py-6" data-testid="see-all-events-btn">
                {t('homeExt.seeAllEvents')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Calendrier Annuel */}
      <section className="py-20 bg-white" data-testid="calendar-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-lime-100 text-lime-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">
              {t('homeExt.allYear')}
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              {t('homeExt.seasonTitle')}
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              {t('homeExt.seasonDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {seasons.map((season, index) => (
              <Card key={index} className="border-0 shadow-lg overflow-hidden" data-testid={`season-${index}`}>
                <div className={`h-2 bg-gradient-to-r ${season.color}`} />
                <CardContent className="p-6">
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-zinc-900 mb-4">
                    {season.name}
                  </h3>
                  <ul className="space-y-2">
                    {season.events.map((event, eIndex) => (
                      <li key={eIndex} className="flex items-center gap-2 text-sm text-zinc-600">
                        <CheckCircle className="h-4 w-4 text-lime-500" />
                        {event}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section - Améliorée avec intérêts */}
      <section id="newsletter-section" className="py-20 bg-gradient-to-br from-fuchsia-600 via-fuchsia-700 to-purple-800 text-white" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <Mail className="h-12 w-12 mx-auto mb-4 text-lime-400" />
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold mb-4">
              {t('homeExt.joinCommunity')}
            </h2>
            <p className="text-white/90 text-lg">
              {t('homeExt.newsletterDesc')}
            </p>
          </div>

          {newsletterSubmitted ? (
            <div className="text-center py-8">
              <CheckCircle className="h-16 w-16 mx-auto mb-4 text-lime-400" />
              <h3 className="text-2xl font-bold mb-2">{t('homeExt.thankYou')}</h3>
              <p className="text-white/90">{t('homeExt.subscribedMsg')}</p>
            </div>
          ) : (
            <Card className="bg-white/10 backdrop-blur-sm border-0">
              <CardContent className="p-8">
                <form onSubmit={handleNewsletterSubmit} className="space-y-6">
                  <div>
                    <Label className="text-white mb-3 block">{t('homeExt.iWantInfo')}</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {newsletterInterests.map((interest) => (
                        <label 
                          key={interest.id}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            newsletterInterestsSelected.includes(interest.id)
                              ? 'bg-lime-500/30 border-2 border-lime-400'
                              : 'bg-white/10 border-2 border-transparent hover:bg-white/20'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={newsletterInterestsSelected.includes(interest.id)}
                            onChange={() => toggleInterest(interest.id)}
                            className="w-4 h-4 rounded accent-lime-500"
                          />
                          <span className="text-white text-sm">
                            {interest.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Input
                      type="email"
                      placeholder={t('homeExt.emailPlaceholder')}
                      value={newsletterEmail}
                      onChange={(e) => setNewsletterEmail(e.target.value)}
                      required
                      className="flex-1 bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-lime-400"
                      data-testid="newsletter-email"
                    />
                    <Button
                      type="submit"
                      disabled={newsletterLoading}
                      className="bg-lime-500 hover:bg-lime-400 text-white rounded-full px-8"
                      data-testid="newsletter-submit"
                    >
                      {newsletterLoading ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <>
                          {t('homeExt.joinCommunityBtn')}
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Bénévoles Section */}
      <section className="py-20 bg-white" data-testid="benevoles-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-2 bg-lime-100 text-lime-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">
                {t('homeExt.joinTeam')}
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
                {t('homeExt.becomeVolunteerTitle')}
              </h2>
              <p className="text-zinc-600 mb-6 leading-relaxed">
                {t('homeExt.volunteerDesc')}
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-zinc-700">
                  <CheckCircle className="h-5 w-5 text-lime-500" />
                  {t('homeExt.volunteerBenefit1')}
                </li>
                <li className="flex items-center gap-3 text-zinc-700">
                  <CheckCircle className="h-5 w-5 text-lime-500" />
                  {t('homeExt.volunteerBenefit2')}
                </li>
                <li className="flex items-center gap-3 text-zinc-700">
                  <CheckCircle className="h-5 w-5 text-lime-500" />
                  {t('homeExt.volunteerBenefit3')}
                </li>
              </ul>
              <Link to="/benevoles">
                <Button className="bg-lime-600 hover:bg-lime-700 text-white rounded-full px-8 py-6 text-lg" data-testid="benevoles-cta">
                  <HandHeart className="mr-2 h-5 w-5" />
                  {t('homeExt.volunteerBtn')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
            <Card className="border-0 shadow-xl bg-gradient-to-br from-lime-500 to-green-600 text-white p-8">
              <div className="text-center">
                <HandHeart className="h-16 w-16 mx-auto mb-4" />
                <p className="text-5xl font-bold mb-2">30+</p>
                <p className="text-lg text-white/90">{t('homeExt.volunteersTrustUs')}</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Découvrir Sanxay */}
      <section className="py-20 bg-zinc-50" data-testid="discover-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-fuchsia-100 text-fuchsia-600 rounded-full text-sm font-medium uppercase tracking-widest mb-4">
              {t('homeExt.heritageNature')}
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              {t('homeExt.discoverSanxay')}
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              {t('homeExt.discoverDesc')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {touristSites.map((site, index) => (
              <Card key={index} className="border-0 shadow-lg hover:-translate-y-1 transition-all" data-testid={`site-${index}`}>
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gradient-to-br from-fuchsia-100 to-lime-100 flex items-center justify-center">
                    <site.icon className="h-7 w-7 text-fuchsia-600" />
                  </div>
                  <h3 className="font-bold text-zinc-900 mb-1">{site.name}</h3>
                  <p className="text-sm text-lime-600 font-medium">{site.distance}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/venir">
              <Button variant="outline" className="border-2 border-fuchsia-500 text-fuchsia-600 hover:bg-fuchsia-50 rounded-full px-8">
                {t('homeExt.discoverRegion')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-sm font-medium uppercase tracking-widest text-fuchsia-500 mb-4 block">
              {t('homeExt.ourCommitment')}
            </span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
              {t('homeExt.valuesTitle')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="group hover:-translate-y-2 transition-all duration-300 border-0 shadow-lg shadow-zinc-100 bg-white" data-testid={`value-card-${index}`}>
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-fuchsia-100 to-lime-100 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <value.icon className="h-8 w-8 text-fuchsia-600" />
                  </div>
                  <h3 className="font-['Playfair_Display'] text-xl font-bold text-zinc-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-zinc-500 text-sm">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Location Preview */}
      <section className="py-20 bg-gradient-to-b from-white to-zinc-50" data-testid="location-preview-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium uppercase tracking-widest text-lime-600 mb-4 block">
                {t('homeExt.ourPlace')}
              </span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
                {t('homeExt.domainTitle')}
              </h2>
              <p className="text-zinc-600 mb-6 leading-relaxed">
                {t('homeExt.domainDesc')}
              </p>
              <div className="flex items-start gap-3 mb-6">
                <MapPin className="h-5 w-5 text-fuchsia-500 flex-shrink-0 mt-1" />
                <div>
                  <p className="font-medium text-zinc-900">Domaine de la Briouse</p>
                  <p className="text-zinc-500">La Briouse, 86600 Sanxay</p>
                </div>
              </div>
              <Link to="/venir">
                <Button className="btn-primary" data-testid="location-preview-btn">
                  {t('homeExt.howToCome')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2762.5!2d-0.0395!3d46.4875!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48047487e5555555%3A0x1234567890abcdef!2sLa%20Briouse%2C%2086600%20Sanxay!5e0!3m2!1sfr!2sfr!4v1700000000000!5m2!1sfr!2sfr"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Domaine de la Briouse - La Briouse 86600 Sanxay"
                  className="rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-fuchsia-600 to-fuchsia-700" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center text-white">
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold mb-6">
            {t('homeExt.joinAdventure')}
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            {t('homeExt.ctaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/adhesion">
              <Button className="bg-white text-fuchsia-600 hover:bg-fuchsia-50 rounded-full px-10 py-6 text-lg font-medium" data-testid="cta-adhesion-btn">
                {t('homeExt.memberFor10')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-white/50 text-white hover:bg-white/10 rounded-full px-10 py-6 text-lg" data-testid="cta-contact-btn">
                {t('homeExt.contactUsBtn')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Bouton flottant WhatsApp/Téléphone */}
      <div className="fixed bottom-24 right-24 z-40 flex flex-col gap-3" data-testid="floating-buttons">
        <a
          href="tel:+33749513090"
          className="w-11 h-11 rounded-full bg-lime-600 hover:bg-lime-700 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-110"
          title={t('homeExt.call')}
          data-testid="floating-phone"
        >
          <Phone className="h-5 w-5" />
        </a>
        <a
          href="https://wa.me/33749513090"
          target="_blank"
          rel="noopener noreferrer"
          className="w-11 h-11 rounded-full bg-green-500 hover:bg-green-600 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-110"
          title="WhatsApp"
          data-testid="floating-whatsapp"
        >
          <MessageCircle className="h-5 w-5" />
        </a>
      </div>
    </div>
  );
}
